/**
 * EditUser Component
 * Page for viewing/editing user details (role, department, manager, etc.)
 * Supports both view-only and edit modes based on URL path
 */

import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useOpenWithReturn, useGoBack } from '../../hooks/useReturnNavigation';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useUser, useUpdateUser, useUsers } from '../../hooks';
import { Card, Button, Input, Select, LoadingSpinner, Badge } from '../../components/common';
import { UserDepartments, UserTeams } from '../../components/users';
import type { UserRole } from '../../types/api';

export const EditUser: React.FC = () => {
  // Back, Cancel and Save return to the view that opened this page (utils/returnTo.ts).
  const goBack = useGoBack('/admin?tab=users');
  const openWithReturn = useOpenWithReturn();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const { data: user, isLoading: isLoadingUser } = useUser(id || '');
  const updateUserMutation = useUpdateUser();

  // Determine if we're in view-only or edit mode based on URL
  const isViewOnly = !location.pathname.endsWith('/edit');

  // Get list of users for manager selection
  const { data: usersData } = useUsers({ limit: 100 });

  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    role: UserRole;
    managerId: string;
  }>({
    firstName: '',
    lastName: '',
    role: 'employee',
    managerId: '',
  });

  // Load user data when available
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        role: (user.role as UserRole) || 'employee',
        managerId: user.managerId || '',
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) {
      toast.error('User ID is missing');
      return;
    }

    // Validate required fields
    if (!formData.firstName?.trim()) {
      toast.error('First name is required');
      return;
    }

    if (!formData.lastName?.trim()) {
      toast.error('Last name is required');
      return;
    }

    try {
      await updateUserMutation.mutateAsync({
        id,
        data: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          managerId: formData.managerId || null,
        },
      });
      toast.success('User updated successfully');
      goBack();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update user';
      toast.error(errorMessage);
    }
  };

  if (isLoadingUser) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold text-stone-900 mb-2">User Not Found</h2>
          <p className="text-stone-500 mb-4">The user you're looking for doesn't exist.</p>
          <Button onClick={goBack}>Back to Users</Button>
        </Card>
      </div>
    );
  }

  // Build manager options (exclude current user)
  const managerOptions = [
    { value: '', label: 'No Manager', disabled: false },
    ...(usersData?.users
      .filter(u => u.id !== id && (u.role === 'manager' || u.role === 'account_admin'))
      .map(u => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName}`,
        disabled: false,
      })) || []),
  ];


  // Check if current user can edit this user
  const canEdit = currentUser && (
    currentUser.role === 'account_admin' ||
    (currentUser.role === 'manager' && user?.role !== 'account_admin')
  );

  // Get status badge variant
  const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'danger' | 'secondary' => {
    switch (status) {
      case 'active': return 'success';
      case 'invited': return 'warning';
      case 'inactive': return 'danger';
      default: return 'secondary';
    }
  };

  // Get role badge variant
  const getRoleBadgeVariant = (role: string): 'primary' | 'warning' | 'secondary' => {
    switch (role) {
      case 'account_admin': return 'primary';
      case 'manager': return 'warning';
      default: return 'secondary';
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-gradient-to-r from-stone-800 via-stone-700 to-primary-600">
        <div className="px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Button
                variant="ghost"
                onClick={goBack}
                className="mr-4 text-white hover:bg-white/10"
              >
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-semibold text-white" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
                  {isViewOnly ? 'User Details' : 'Edit User'}
                </h1>
                <p className="text-sm text-stone-300 mt-1">
                  {user.email}
                </p>
              </div>
            </div>
            {isViewOnly && canEdit && (
              <Button
                variant="secondary"
                onClick={() => openWithReturn(`/users/${id}/edit`)}
                className="bg-white text-stone-800 hover:bg-stone-100"
              >
                Edit User
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-8 py-8">
        <div className="max-w-2xl mx-auto space-y-6">
          <Card className="p-6">
            {isViewOnly ? (
              // View-only mode
              <div className="space-y-6">
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center text-2xl font-semibold text-primary-700">
                  {user.firstName[0]}{user.lastName[0]}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-stone-900">
                    {user.firstName} {user.lastName}
                  </h2>
                  <p className="text-stone-500">{user.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-stone-500 mb-1">Role</label>
                  <Badge variant={getRoleBadgeVariant(user.role)}>
                    {user.role.replace('_', ' ')}
                  </Badge>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-500 mb-1">Status</label>
                  <Badge variant={getStatusBadgeVariant(user.status)}>
                    {user.status}
                  </Badge>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-500 mb-1">Manager</label>
                  <p className="text-stone-900">
                    {user.managerId
                      ? usersData?.users.find(u => u.id === user.managerId)
                          ? `${usersData.users.find(u => u.id === user.managerId)!.firstName} ${usersData.users.find(u => u.id === user.managerId)!.lastName}`
                          : '-'
                      : '-'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-500 mb-1">Last Login</label>
                  <p className="text-stone-900">
                    {user.lastLoginAt
                      ? new Date(user.lastLoginAt).toLocaleDateString()
                      : 'Never'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-500 mb-1">Member Since</label>
                  <p className="text-stone-900">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="secondary"
                  onClick={goBack}
                >
                  Back to Users
                </Button>
                {canEdit && (
                  <Button
                    variant="primary"
                    onClick={() => openWithReturn(`/users/${id}/edit`)}
                  >
                    Edit User
                  </Button>
                )}
              </div>
            </div>
          ) : (
            // Edit mode
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                />
                <Input
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>

              <Select
                label="Role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                options={[
                  { value: 'account_admin', label: 'Account Admin', disabled: false },
                  { value: 'manager', label: 'Manager', disabled: false },
                  { value: 'employee', label: 'Employee', disabled: false },
                  { value: 'viewer', label: 'Viewer', disabled: false },
                ]}
              />

              <Select
                label="Manager"
                name="managerId"
                value={formData.managerId}
                onChange={handleChange}
                options={managerOptions}
              />

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={goBack}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={updateUserMutation.isPending}
                >
                  {updateUserMutation.isPending ? (
                    <span className="flex items-center">
                      <LoadingSpinner size="sm" className="mr-2" />
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </form>
          )}
          </Card>

          {/* Multi-department management section */}
          {id && <UserDepartments userId={id} canEdit={!isViewOnly && !!canEdit} />}

          {/* Multi-team management section */}
          {id && <UserTeams userId={id} canEdit={!isViewOnly && !!canEdit} />}
        </div>
      </div>
    </div>
  );
};
